/**
 * The maneuver baseline as something you can SEE on the map and in the timeseries.
 *
 * The stored `fit_json` answers "what number should this maneuver have had" — that is what the scatter
 * curve and the table columns read. It cannot answer "what should the turn have looked like", because
 * a fitted channel value is not a track. So for one selected wind speed this module goes back to the
 * member maneuvers the baseline was saved from, fetches their real map and timeseries tracks, and
 * averages them into ONE reference path and ONE reference trace per chart group.
 *
 * Members are Gaussian-weighted by how close their own TWS is to the selected bin
 * (`kernelWeightsForTws`), so picking 20 kph shows the baseline as it was actually sailed in 20 kph
 * rather than a blend of the whole range. The same weights drive the path and every trace, which is
 * what makes them describe the same set of maneuvers.
 *
 * TWS = ALL has no single reference wind speed, so nothing here runs — the scatter draws the whole
 * fitted curve instead.
 *
 * Ported from Alinghi's `frontend/utils/maneuverBaseline.ts`. The one structural difference: Alinghi
 * fetches its member list from `baselines.maneuver_json` on demand, whereas RaceSight's `fit_json` is
 * already in hand and carries the members per CONFIG + maneuver type — so the caller passes them in
 * and there is no second fetch and no series cache.
 */

import { fetchMapData, fetchTimeSeriesData } from '../services/maneuversDataService';
import type { ManeuversContext } from '../utils/maneuversConfig';
import type { ManeuverBaselineMember } from '../services/baselinesService';
import { kernelWeightsForTws } from './maneuverBaselineFit';
import { buildTimeseriesDescription, fetchManeuverTimeseriesOptions } from './maneuverTimeseriesGroups';
import { debug, warn as logWarning } from './console';
import { round } from './global';

/** One point of an averaged ground track. `lat`/`lng` only — the drawing code builds its own LatLng. */
export interface ManeuverBaselineMapPoint {
  time: number;
  lat: number;
  lng: number;
}

/** One sample of an averaged timeseries trace: `time` plus whichever numeric channels the members had. */
export interface ManeuverBaselineTimeseriesPoint {
  time: number;
  [channel: string]: number;
}

/**
 * Everything drawable for one (baseline, config, type, wind speed).
 *
 * `path` OR `pathsBySide` is populated, never both — see `loadManeuverBaselineTracks`.
 */
export interface ManeuverBaselineTracks {
  path: ManeuverBaselineMapPoint[];
  pathsBySide: { port: ManeuverBaselineMapPoint[]; stbd: ManeuverBaselineMapPoint[] };
  /** The default (BASICS) trace, for callers that don't group. */
  timeseries: ManeuverBaselineTimeseriesPoint[];
  timeseriesByGroup: Record<string, ManeuverBaselineTimeseriesPoint[]>;
}

export function emptyManeuverBaselineTracks(): ManeuverBaselineTracks {
  return { path: [], pathsBySide: { port: [], stbd: [] }, timeseries: [], timeseriesByGroup: {} };
}

/** Whether anything is actually drawable on a map. */
export function maneuverBaselineHasPath(
  data: ManeuverBaselineTracks | null | undefined
): boolean {
  if (!data) { return false; }
  return data.path.length > 1
    || data.pathsBySide.port.length > 1
    || data.pathsBySide.stbd.length > 1;
}

/**
 * CSS class for a baseline track.
 *
 * The single mirrored path is solid. `side` is passed only for the Standard map view, which draws one
 * path per tack — port dotted, starboard solid — because mirror-image ground tracks cannot be merged.
 */
export function baselineTrackCssClass(side?: 'port' | 'stbd'): string {
  if (side === 'port') { return 'maneuver-baseline-track maneuver-baseline-track--ps'; }
  return 'maneuver-baseline-track maneuver-baseline-track--sp';
}

/** The averaged trace for one chart group (BASICS, FOILS, …), falling back to the default trace. */
export function baselineTimeseriesForGroup(
  data: Pick<ManeuverBaselineTracks, 'timeseries' | 'timeseriesByGroup'> | null | undefined,
  groupKey: string | undefined
): ManeuverBaselineTimeseriesPoint[] {
  if (!data) { return []; }
  const key = (groupKey || 'BASICS').trim().toUpperCase();
  const byGroup = data.timeseriesByGroup;
  if (byGroup[key] && byGroup[key].length >= 2) { return byGroup[key]; }
  return data.timeseries;
}

/** One channel's value from an averaged point, matching case-insensitively. */
export function readBaselineTimeseriesValue(
  point: ManeuverBaselineTimeseriesPoint,
  channel: string
): number | null {
  const wanted = String(channel || '').toLowerCase();
  if (wanted === '') { return null; }
  for (const key of Object.keys(point)) {
    if (key.toLowerCase() === wanted) {
      const v = Number(point[key]);
      return Number.isFinite(v) ? v : null;
    }
  }
  return null;
}

/**
 * The averaged trace that actually carries a channel, searched across every chart group.
 *
 * Charts are grouped (BASICS, FOILS, …) and each group's payload carries its own channels — but the
 * per-chart drawing code is a sibling of the per-group build, so it does not know which group it is
 * in. Looking the channel up across the groups is equivalent and needs no plumbing: a channel lives
 * in whichever group recorded it, and every group's trace averages the same member maneuvers.
 *
 * Returns an empty array when no group covers the channel — that chart then draws no reference,
 * which is right: a flat line at zero would invent one.
 */
export function baselineTimeseriesForChannel(
  data: Pick<ManeuverBaselineTracks, 'timeseries' | 'timeseriesByGroup'> | null | undefined,
  channel: string
): ManeuverBaselineTimeseriesPoint[] {
  if (!data) { return []; }
  const covers = (points: ManeuverBaselineTimeseriesPoint[] | undefined): boolean => {
    if (!points || points.length < 2) { return false; }
    let seen = 0;
    for (const p of points) {
      if (readBaselineTimeseriesValue(p, channel) != null && ++seen >= 2) { return true; }
    }
    return false;
  };

  // BASICS (or whatever the default trace is) first, so the common case does not scan every group.
  if (covers(data.timeseries)) { return data.timeseries; }
  for (const points of Object.values(data.timeseriesByGroup)) {
    if (covers(points)) { return points; }
  }
  return [];
}

/**
 * Min and max of the baseline trace a timeseries chart will actually draw for `channel`.
 *
 * Empty when that chart draws no reference (baseline off, still loading, or the channel is not in
 * the averaged trace). The chart folds this into its y domain so the line is on scale.
 */
export function baselineTimeseriesChannelExtent(
  data: Pick<ManeuverBaselineTracks, 'timeseries' | 'timeseriesByGroup'> | null | undefined,
  channel: string
): { min: number; max: number } | null {
  const points = baselineTimeseriesForChannel(data, channel);
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    const value = readBaselineTimeseriesValue(point, channel);
    if (value == null) { continue; }
    if (value < min) { min = value; }
    if (value > max) { max = value; }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) { return null; }
  return { min, max };
}

/** The map payload description for a phase, matching what the maneuver Map itself requests. */
export function buildMapDescription(currentPhase: string, isNormalized: boolean): string {
  const suffix = isNormalized ? 'Normalized' : 'Standard';
  if (currentPhase === 'INVESTMENT') { return `1_${suffix}`; }
  if (currentPhase === 'TURN') { return `2_${suffix}`; }
  if (currentPhase === 'ACCELERATION') { return `3_${suffix}`; }
  return `0_${suffix}`;
}

function isNumeric(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** Bounds on the resampling step, seconds. The stored tracks sit at roughly half-second spacing. */
const RESAMPLE_MIN_STEP_S = 0.25;
const RESAMPLE_MAX_STEP_S = 1;

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/**
 * A common time grid across several tracks: the INTERSECTION of their time ranges, stepped at their
 * median spacing.
 *
 * The intersection is what keeps the average honest — no track is ever extrapolated to fill a time it
 * does not cover, so the averaged path cannot drift toward whichever maneuver happened to be recorded
 * longest.
 */
export function commonTimeGrid<T extends { time: number }>(tracks: T[][]): number[] {
  const usable = tracks.filter((t) => t && t.length >= 2);
  if (usable.length === 0) { return []; }

  let lo = -Infinity;
  let hi = Infinity;
  const spacings: number[] = [];
  for (const track of usable) {
    const times = track.map((p) => Number(p.time)).filter((t) => Number.isFinite(t)).sort((a, b) => a - b);
    if (times.length < 2) { return []; }
    lo = Math.max(lo, times[0]);
    hi = Math.min(hi, times[times.length - 1]);
    for (let i = 1; i < times.length; i++) {
      const d = times[i] - times[i - 1];
      if (d > 0) { spacings.push(d); }
    }
  }
  if (!(hi > lo) || spacings.length === 0) { return []; }

  const step = Math.min(RESAMPLE_MAX_STEP_S, Math.max(RESAMPLE_MIN_STEP_S, median(spacings)));
  const out: number[] = [];
  for (let t = lo; t <= hi + 1e-9; t += step) {
    out.push(Math.round(t * 100) / 100);
  }
  return out;
}

/** Linear interpolation of one numeric field of a track at a time, or null outside its range. */
export function sampleTrackField<T extends { time: number }>(
  sorted: T[],
  time: number,
  field: keyof T & string
): number | null {
  if (sorted.length === 0) { return null; }
  const first = Number(sorted[0].time);
  const last = Number(sorted[sorted.length - 1].time);
  if (time < first - 1e-9 || time > last + 1e-9) { return null; }
  for (let i = 1; i < sorted.length; i++) {
    if (time <= Number(sorted[i].time) + 1e-9) {
      const a = sorted[i - 1];
      const b = sorted[i];
      const av = Number(a[field]);
      const bv = Number(b[field]);
      if (!Number.isFinite(av) || !Number.isFinite(bv)) { return Number.isFinite(av) ? av : null; }
      const at = Number(a.time);
      const bt = Number(b.time);
      const f = bt === at ? 0 : (time - at) / (bt - at);
      return av + f * (bv - av);
    }
  }
  const lastVal = Number(sorted[sorted.length - 1][field]);
  return Number.isFinite(lastVal) ? lastVal : null;
}

/** Weighted mean of value/weight pairs, ignoring nulls. */
export function weightedMeanOf(pairs: Array<{ v: number | null; w: number }>): number | null {
  let sw = 0;
  let sv = 0;
  for (const { v, w } of pairs) {
    if (v == null || !Number.isFinite(v) || !(w > 0)) { continue; }
    sw += w;
    sv += w * v;
  }
  return sw > 0 ? sv / sw : null;
}

/** One member's track, tagged with the maneuver it came from. */
export interface BaselineTrack<T> {
  eventId: number | null;
  points: T[];
}

/**
 * Tracks paired with their kernel weight, dropping any that weigh nothing.
 *
 * A track whose event id is unreadable keeps weight 1 rather than being dropped: it is still a
 * maneuver the baseline was built from, and the alternative silently shrinks the average.
 */
export function weighTracks<T>(
  tracks: Array<BaselineTrack<T>>,
  weightByEventId: Map<number, number>
): Array<{ points: T[]; w: number }> {
  return tracks
    .map((track) => {
      const w = track.eventId != null ? weightByEventId.get(track.eventId) : undefined;
      return { points: track.points, w: w != null && w > 0 ? w : 1 };
    })
    .filter((t) => t.points.length > 0 && t.w > 0);
}

/**
 * One path from several, weighted. Resamples every track onto a common time grid and takes the
 * weighted mean position at each step.
 *
 * Deliberately not a strict-intersection average over exact timestamps: one track sampled a
 * hundredth of a second off would drop that timestamp for all of them, and it gets worse the more
 * members a wind speed selects.
 */
export function averageMapTracks(
  weighted: Array<{ points: ManeuverBaselineMapPoint[]; w: number }>
): ManeuverBaselineMapPoint[] {
  if (weighted.length === 0) { return []; }
  if (weighted.length === 1) { return weighted[0].points; }

  const sorted = weighted.map(({ points, w }) => ({
    points: [...points].sort((a, b) => a.time - b.time),
    w,
  }));
  const grid = commonTimeGrid(sorted.map((s) => s.points));
  if (grid.length < 2) {
    // No overlapping window at all — one real track beats a line through nothing.
    return sorted[0].points;
  }

  const out: ManeuverBaselineMapPoint[] = [];
  for (const time of grid) {
    const lat = weightedMeanOf(sorted.map(({ points, w }) => ({ v: sampleTrackField(points, time, 'lat'), w })));
    const lng = weightedMeanOf(sorted.map(({ points, w }) => ({ v: sampleTrackField(points, time, 'lng'), w })));
    if (lat == null || lng == null) { continue; }
    out.push({ time, lat, lng });
  }
  return out;
}

/** The same, restricted to the members on one tack side — Standard map view only. */
export function averageMapTracksForSide(
  tracks: Array<BaselineTrack<ManeuverBaselineMapPoint>>,
  weightByEventId: Map<number, number>,
  sideByEventId: Map<number, 'port' | 'stbd'>,
  side: 'port' | 'stbd'
): ManeuverBaselineMapPoint[] {
  // A track whose side is unknown is left out rather than guessed: putting a port track into the
  // starboard average is exactly the mistake the two-path Standard view exists to avoid.
  const ofSide = tracks.filter((t) => t.eventId != null && sideByEventId.get(t.eventId) === side);
  return averageMapTracks(weighTracks(ofSide, weightByEventId));
}

/** One trace from several, weighted, over every numeric channel the tracks carry. */
export function averageTimeseriesTracks(
  weighted: Array<{ points: ManeuverBaselineTimeseriesPoint[]; w: number }>
): ManeuverBaselineTimeseriesPoint[] {
  if (weighted.length === 0) { return []; }

  const sorted = weighted.map(({ points, w }) => ({
    points: [...points].sort((a, b) => Number(a.time) - Number(b.time)),
    w,
  }));
  if (sorted.length === 1) { return sorted[0].points; }

  const grid = commonTimeGrid(sorted.map((s) => s.points));
  if (grid.length < 2) { return sorted[0].points; }

  const channels = new Set<string>();
  sorted.forEach(({ points }) => {
    points.forEach((point) => {
      Object.keys(point).forEach((k) => {
        if (k !== 'time' && k !== 'event_id' && isNumeric(point[k])) { channels.add(k); }
      });
    });
  });

  return grid.map((time) => {
    const out: ManeuverBaselineTimeseriesPoint = { time };
    channels.forEach((channel) => {
      const v = weightedMeanOf(
        sorted.map(({ points, w }) => ({ v: sampleTrackField(points, time, channel), w }))
      );
      if (v != null) { out[channel] = round(v, 2); }
    });
    return out;
  });
}

function readMapJsonLatLng(d: Record<string, unknown>): { lat: number; lng: number } | null {
  const latRaw = d.lat ?? d.Lat ?? d.LAT ?? d.latitude;
  const lngRaw = d.lng ?? d.lon ?? d.Lon ?? d.LON ?? d.LNG ?? d.longitude;
  const lat = parseFloat(String(latRaw));
  const lng = parseFloat(String(lngRaw));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) { return null; }
  return { lat, lng };
}

export function processMapPayloadItem(item: Record<string, unknown>): ManeuverBaselineMapPoint[] {
  const json = item.json as { values?: Record<string, unknown>[] } | undefined;
  if (!json?.values?.length) { return []; }
  return json.values
    .map((d) => {
      const coords = readMapJsonLatLng(d);
      const time = parseFloat(String(d.time ?? d.Time));
      if (!coords || !Number.isFinite(time)) { return null; }
      return { lat: coords.lat, lng: coords.lng, time };
    })
    .filter((p): p is ManeuverBaselineMapPoint => p !== null);
}

export function processTimeseriesPayloadItem(
  item: Record<string, unknown>
): ManeuverBaselineTimeseriesPoint[] {
  const json = item.json as { values?: Record<string, unknown>[] } | undefined;
  if (!json?.values?.length) { return []; }
  return json.values
    .map((d) => {
      const time = parseFloat(String(d.time));
      if (!Number.isFinite(time)) { return null; }
      const point: ManeuverBaselineTimeseriesPoint = { time };
      Object.keys(d).forEach((k) => {
        if (k === 'time') { return; }
        const v = d[k];
        if (isNumeric(v)) { point[k] = round(v, 2); }
      });
      return point;
    })
    .filter((p): p is ManeuverBaselineTimeseriesPoint => p !== null);
}

/** The payload item's own event id, which the processors do not copy onto each point. */
function trackEventId(item: Record<string, unknown>): number | null {
  const raw = Number(item?.event_id ?? (item as { eventId?: unknown }).eventId);
  return Number.isFinite(raw) && raw > 0 ? raw : null;
}

/**
 * Which side a member's STANDARD map track belongs to.
 *
 * Uses the `side` stored on the member (sign of `Twa_entry`, per `sideForEntryTwa` in
 * server_app/utils/maneuverBaselineFit.js). Note the pipeline's own mirroring decision is made on
 * `Twa_start`, not `Twa_entry`; the two normally agree, and a member where they disagree lands in the
 * wrong side's average. Fixing that means storing `twa_start` on the members and re-saving every
 * baseline, so it is accepted for now — the cost is one member's weight in one of two averages.
 */
function memberMapSide(member: ManeuverBaselineMember): 'port' | 'stbd' | null {
  if (member.side === 1) { return 'stbd'; }
  if (member.side === -1) { return 'port'; }
  return null;
}

const BASELINE_TRACK_CONTEXT_FALLBACKS: ManeuversContext[] = ['dataset', 'fleet', 'historical'];

function baselineFetchContexts(primary: ManeuversContext): ManeuversContext[] {
  return [...new Set([primary, ...BASELINE_TRACK_CONTEXT_FALLBACKS.filter((c) => c !== primary)])];
}

async function fetchMapTracksForEventIds(params: {
  context: ManeuversContext;
  eventType: string;
  mapDescription: string;
  eventIds: number[];
  signal?: AbortSignal;
}): Promise<Array<BaselineTrack<ManeuverBaselineMapPoint>>> {
  if (params.eventIds.length === 0) { return []; }

  const fetchParams = {
    eventType: params.eventType,
    description: params.mapDescription,
    eventList: params.eventIds,
    signal: params.signal,
  };

  for (const ctx of baselineFetchContexts(params.context)) {
    // DELIBERATELY no Normalized <-> Standard fallback. Substituting the other mirror variant when the
    // requested one comes back empty is not a fallback but a different geometry: the Normalized
    // payload folds both tacks onto one side, the Standard one does not. Substituting would put BOTH
    // per-side averages on the same side of the Standard map, or draw the single mirrored path down
    // the middle of two mirror-image groups. If the variant the view needs is missing, the honest
    // answer is no baseline track.
    const mapPayload = (await fetchMapData(ctx, fetchParams)) as Record<string, unknown>[];

    const tracks = mapPayload
      .map((item) => ({ eventId: trackEventId(item), points: processMapPayloadItem(item) }))
      .filter((t) => t.points.length > 0);
    if (tracks.length > 0) {
      if (ctx !== params.context) {
        debug('maneuverBaselineTracks: map tracks loaded via fallback context', ctx);
      }
      return tracks;
    }
  }

  // The view's mirror variant simply is not stored for these maneuvers. Warned rather than silent,
  // since the symptom is a missing baseline track. Not when the load was superseded though:
  // cancellation reads as an empty payload here (getData resolves rather than throws on abort), and
  // claiming the description is missing would then simply be false.
  if (!params.signal?.aborted) {
    logWarning(
      'maneuverBaselineTracks: no map tracks for description %s across %d member(s) — no baseline path will draw',
      params.mapDescription,
      params.eventIds.length
    );
  }
  return [];
}

async function fetchTimeseriesTracksForEventIds(params: {
  context: ManeuversContext;
  eventType: string;
  tsDescription: string;
  eventIds: number[];
  signal?: AbortSignal;
}): Promise<Array<BaselineTrack<ManeuverBaselineTimeseriesPoint>>> {
  if (params.eventIds.length === 0) { return []; }

  const fetchParams = {
    eventType: params.eventType,
    description: params.tsDescription,
    eventList: params.eventIds,
    signal: params.signal,
  };

  for (const ctx of baselineFetchContexts(params.context)) {
    const tsJson = await fetchTimeSeriesData(ctx, fetchParams);
    const tracks = (tsJson as Record<string, unknown>[])
      .map((item) => ({ eventId: trackEventId(item), points: processTimeseriesPayloadItem(item) }))
      .filter((t) => t.points.length > 0);
    if (tracks.length > 0) {
      if (ctx !== params.context) {
        debug('maneuverBaselineTracks: timeseries tracks loaded via fallback context', ctx);
      }
      return tracks;
    }
  }

  return [];
}

/**
 * Loaded track sets, keyed by everything that changes them.
 *
 * Module-level because the maneuver pages re-run their loaders on filter churn that has nothing to do
 * with the baseline, and refetching a dozen tracks each time is what made this feel slow.
 */
const trackCache = new Map<string, ManeuverBaselineTracks>();

function trackCacheKey(parts: {
  baselineId: number;
  config: string;
  eventType: string;
  tws: number;
  mapDescription: string;
  groupKeys: string[];
}): string {
  // The CONFIG belongs in the key: RaceSight's members are per config, so two configs of one baseline
  // are two different member sets and must not share an average.
  return [
    parts.baselineId,
    parts.config,
    parts.eventType,
    parts.tws,
    parts.mapDescription,
    parts.groupKeys.join(','),
  ].join('|');
}

/** Drop every cached track set. Called whenever the selection or the geometry request changes. */
export function clearManeuverBaselineTrackCache(): void {
  trackCache.clear();
}

/**
 * The baseline as drawn for ONE wind speed: one path (or one per tack) and one trace per chart group.
 *
 * The map is the one place tack side still matters. In the mirrored (Normalized) view both tacks fold
 * onto one side and average into a single path; in Standard view they trace mirror-image ground tracks
 * that cannot honestly be merged, so that view gets one path per side instead.
 *
 * The timeseries gets one trace across both sides: the stored per-maneuver timeseries is already
 * side-normalized by the pipeline, so a port and a starboard trace are directly averageable.
 */
export async function loadManeuverBaselineTracks(params: {
  context: ManeuversContext;
  className: string;
  projectId: number;
  baselineId: number;
  config: string;
  eventType: string;
  /** The members of the selected config + type bucket, straight off the stored fit. */
  members: ManeuverBaselineMember[];
  /** The selected wind speed (bin centre), kph. */
  tws: number;
  phase: string;
  isNormalized: boolean;
  signal?: AbortSignal;
}): Promise<ManeuverBaselineTracks> {
  if (params.members.length === 0) {
    debug('maneuverBaselineTracks: no members for', params.config, params.eventType);
    return emptyManeuverBaselineTracks();
  }

  const selection = kernelWeightsForTws(params.members, params.tws);
  const eventIds = selection.members
    .map((m) => Number(m.event_id))
    .filter((id) => Number.isFinite(id) && id > 0);

  const mapDescription = buildMapDescription(params.phase, params.isNormalized);
  const groupKeys = await fetchManeuverTimeseriesOptions(params.className, String(params.projectId));
  const key = trackCacheKey({
    baselineId: params.baselineId,
    config: params.config,
    eventType: params.eventType,
    tws: params.tws,
    mapDescription,
    groupKeys,
  });

  const cached = trackCache.get(key);
  if (cached) { return cached; }

  if (eventIds.length === 0) {
    debug('maneuverBaselineTracks: no fetchable member ids near %s kph', params.tws);
    return emptyManeuverBaselineTracks();
  }

  // Weight and side per event_id, so a track can be matched after the fetch returns it in whatever
  // order the API had it in, minus anything with no data.
  const weightByEventId = new Map<number, number>();
  const sideByEventId = new Map<number, 'port' | 'stbd'>();
  selection.members.forEach((member, i) => {
    const id = Number(member.event_id);
    if (!Number.isFinite(id) || id <= 0) { return; }
    weightByEventId.set(id, selection.weights[i]);
    const side = memberMapSide(member);
    if (side) { sideByEventId.set(id, side); }
  });

  const mapTracks = await fetchMapTracksForEventIds({
    context: params.context,
    eventType: params.eventType,
    mapDescription,
    eventIds,
    signal: params.signal,
  });

  // Superseded (the user moved to another TWS bin): nothing below is worth fetching.
  if (params.signal?.aborted) { return emptyManeuverBaselineTracks(); }

  const timeseriesByGroup: Record<string, ManeuverBaselineTimeseriesPoint[]> = {};
  await Promise.all(
    groupKeys.map(async (groupKey) => {
      const tsDescription = buildTimeseriesDescription(params.phase, groupKey);
      const tracks = await fetchTimeseriesTracksForEventIds({
        context: params.context,
        eventType: params.eventType,
        tsDescription,
        eventIds,
        signal: params.signal,
      });
      timeseriesByGroup[groupKey.toUpperCase()] = averageTimeseriesTracks(
        weighTracks(tracks, weightByEventId)
      );
    })
  );

  const basicsKey = groupKeys.includes('BASICS') ? 'BASICS' : groupKeys[0]?.toUpperCase();
  const basics = (basicsKey && timeseriesByGroup[basicsKey]) || [];

  const data: ManeuverBaselineTracks = {
    // Only the mirrored variant folds both tacks onto one side, so only it can average into one path.
    path: params.isNormalized ? averageMapTracks(weighTracks(mapTracks, weightByEventId)) : [],
    pathsBySide: params.isNormalized
      ? { port: [], stbd: [] }
      : {
        port: averageMapTracksForSide(mapTracks, weightByEventId, sideByEventId, 'port'),
        stbd: averageMapTracksForSide(mapTracks, weightByEventId, sideByEventId, 'stbd'),
      },
    timeseries: basics,
    timeseriesByGroup,
  };

  // Never cache a superseded load. Cancellation does not throw here — the service resolves a failed
  // GET into `[]`, or into a PARTIAL track set when it breaks mid-batch — so an aborted load resolves
  // normally with an empty or half-averaged path. Cached under this bin's key, that would become the
  // permanent answer for it, which is how changing TWS leaves the map with no baseline track for good.
  if (!params.signal?.aborted) { trackCache.set(key, data); }

  debug('maneuverBaselineTracks: loaded', {
    baselineId: params.baselineId,
    config: params.config,
    tws: params.tws,
    members: selection.members.length,
    kernelSigma: selection.sigma,
    tracks: mapTracks.length,
    pathPoints: data.path.length,
    portPathPoints: data.pathsBySide.port.length,
    stbdPathPoints: data.pathsBySide.stbd.length,
    mapDescription,
  });
  return data;
}
